import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Typography, Paper, List, ListItem, ListItemText, ListItemAvatar,
    Avatar, TextField, IconButton, Divider, Badge, Chip, Tooltip, Snackbar, Alert
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import notificationSoundFile from '../sounds/notification.mp3';
import chatSendSoundFile from '../sounds/chatsend.mp3';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
const MAX_IMAGE_SIZE = 15 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const CHAT_POLL_INTERVAL = 5000;
const MESSAGE_POLL_INTERVAL = 3000;

const AdminChat = () => {
    const [activeChats, setActiveChats] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [errorSnackbar, setErrorSnackbar] = useState({ open: false, message: '' });
    
    const messagesEndRef = useRef(null);
    const prevMessageCountRef = useRef({});
    const notificationAudioRef = useRef(null);
    const sendAudioRef = useRef(null);
    const isFirstLoad = useRef(true);
    const selectedChatRef = useRef(null);
    const isChatInitialLoad = useRef(true);
    const fileInputRef = useRef(null);
    const lastMessageIdRef = useRef(0);

    useEffect(() => {
        notificationAudioRef.current = new Audio(notificationSoundFile);
        sendAudioRef.current = new Audio(chatSendSoundFile);
    }, []);

    useEffect(() => {
        selectedChatRef.current = selectedChat;
    }, [selectedChat]);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    useEffect(scrollToBottom, [messages, scrollToBottom]);

    const validateImage = useCallback((file) => {
        if (!file) return { valid: false, error: 'No file provided' };
        
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            return { valid: false, error: 'Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed' };
        }
        
        if (file.size > MAX_IMAGE_SIZE) {
            return { valid: false, error: 'Image size must be less than 15MB' };
        }
        
        return { valid: true };
    }, []);

    const handleImageSelection = useCallback((file) => {
        const validation = validateImage(file);
        
        if (!validation.valid) {
            setErrorSnackbar({ open: true, message: validation.error });
            return;
        }
        
        setSelectedImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.onerror = () => {
            setErrorSnackbar({ open: true, message: 'Failed to read image file' });
        };
        reader.readAsDataURL(file);
    }, [validateImage]);

    useEffect(() => {
        const handlePaste = (e) => {
            if (!selectedChat) return;
            
            const items = e.clipboardData?.items;
            if (!items) return;

            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    e.preventDefault();
                    const file = items[i].getAsFile();
                    handleImageSelection(file);
                    break;
                }
            }
        };

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [selectedChat, handleImageSelection]);

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleImageSelection(file);
        }
    };

    const clearImageSelection = useCallback(() => {
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const getAuthHeaders = useCallback(() => {
        const token = localStorage.getItem('jwtToken');
        if (!token) throw new Error('No authentication token found');
        return { Authorization: `Bearer ${token}` };
    }, []);

    const markAsRead = useCallback(async (chatId) => {
        try {
            await axios.put(
                `${API_BASE_URL}/api/chat/${encodeURIComponent(chatId)}/read`, 
                {}, 
                { headers: getAuthHeaders() }
            );
        } catch (err) {
            console.error("Failed to mark chat as read", err);
        }
    }, [getAuthHeaders]);

    const fetchActiveChats = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/chat/active`, {
                headers: getAuthHeaders()
            });
            
            const newChats = res.data.map(chat => {
                if (selectedChatRef.current && chat.id === selectedChatRef.current.id) {
                    return { ...chat, unread: 0 };
                }
                return chat;
            });

            newChats.forEach(chat => {
                const prevCount = prevMessageCountRef.current[chat.id] || 0;
                const currentUnread = chat.unread || 0;
                const isChatOpen = selectedChatRef.current && selectedChatRef.current.id === chat.id;

                if (!isFirstLoad.current && currentUnread > prevCount && !isChatOpen) {
                    notificationAudioRef.current?.play().catch(e => console.error("Audio play failed", e));
                }
                prevMessageCountRef.current[chat.id] = currentUnread;
            });
            
            setActiveChats(newChats);
            isFirstLoad.current = false;
        } catch (err) {
            if (err.response?.status === 401) {
                setErrorSnackbar({ open: true, message: 'Session expired. Please log in again.' });
            }
            console.error("Error fetching chats", err);
        }
    }, [getAuthHeaders]);

    useEffect(() => {
        fetchActiveChats();
        const interval = setInterval(fetchActiveChats, CHAT_POLL_INTERVAL);
        return () => clearInterval(interval);
    }, [fetchActiveChats]);

    useEffect(() => {
        if (!selectedChat) return;
        
        isChatInitialLoad.current = true;
        lastMessageIdRef.current = 0;

        const fetchMessages = async () => {
            try {
                const res = await axios.get(
                    `${API_BASE_URL}/api/chat/${encodeURIComponent(selectedChat.id)}/messages`,
                    { headers: getAuthHeaders() }
                );
                
                const dbMessages = res.data;
                const lastId = lastMessageIdRef.current;
                const newMessages = dbMessages.filter(msg => msg.id > lastId);

                if (isChatInitialLoad.current) {
                    setMessages(dbMessages);
                    isChatInitialLoad.current = false;
                } else if (newMessages.length > 0) {
                    const lastNewMsg = newMessages[newMessages.length - 1];
                    if (lastNewMsg.sender !== 'admin') {
                        notificationAudioRef.current?.play().catch(e => console.error("Audio play failed", e));
                        markAsRead(selectedChat.id);
                    }
                    
                    setMessages(prev => {
                        const existingIds = new Set(prev.map(m => m.id));
                        const uniqueNewMessages = newMessages.filter(m => !existingIds.has(m.id));
                        return [...prev, ...uniqueNewMessages];
                    });
                }

                if (dbMessages.length > 0) {
                    lastMessageIdRef.current = dbMessages[dbMessages.length - 1].id;
                }
            } catch (err) {
                if (err.response?.status === 401) {
                    setErrorSnackbar({ open: true, message: 'Session expired. Please log in again.' });
                }
                console.error("Error fetching messages", err);
            }
        };
        
        fetchMessages();
        const interval = setInterval(fetchMessages, MESSAGE_POLL_INTERVAL);
        return () => clearInterval(interval);
    }, [selectedChat, getAuthHeaders, markAsRead]);

    const handleSelectChat = useCallback((chat) => {
        setSelectedChat(chat);
        setActiveChats(prev => prev.map(c => c.id === chat.id ? { ...c, unread: 0 } : c));
        prevMessageCountRef.current[chat.id] = 0;
        clearImageSelection();
        markAsRead(chat.id);
    }, [clearImageSelection, markAsRead]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        const messageText = newMessage.trim();
        
        if (!messageText && !selectedImage) return;
        if (!selectedChat) return;

        try {
            if (selectedImage) {
                const reader = new FileReader();
                reader.onloadend = async () => {
                    const base64Image = reader.result;

                    sendAudioRef.current?.play().catch(e => console.error("Audio play failed", e));

                    await axios.post(
                        `${API_BASE_URL}/api/chat/${encodeURIComponent(selectedChat.id)}/message`, 
                        { 
                            sender: 'admin', 
                            text: messageText || 'Sent an image',
                            messageType: 'image',
                            imageData: base64Image
                        },
                        { headers: getAuthHeaders() }
                    );

                    clearImageSelection();
                    setNewMessage('');
                };
                reader.onerror = () => {
                    setErrorSnackbar({ open: true, message: 'Failed to process image' });
                };
                reader.readAsDataURL(selectedImage);
            } else {
                await axios.post(
                    `${API_BASE_URL}/api/chat/${encodeURIComponent(selectedChat.id)}/message`, 
                    { sender: 'admin', text: messageText },
                    { headers: getAuthHeaders() }
                );
                setNewMessage('');
                sendAudioRef.current?.play().catch(e => console.error("Audio play failed", e));
            }
        } catch (err) {
            if (err.response?.status === 401) {
                setErrorSnackbar({ open: true, message: 'Session expired. Please log in again.' });
            } else {
                setErrorSnackbar({ open: true, message: 'Failed to send message' });
            }
            console.error("Error sending message", err);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
    };

    const sanitizeText = (text) => {
        if (!text) return '';
        return String(text).replace(/[<>]/g, '');
    };

    return (
        <>
        <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)', gap: 2 }}>
            <Paper sx={{ width: 300, overflowY: 'auto', borderRight: '1px solid #e0e0e0' }}>
                <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#11224d' }}>Active Chats</Typography>
                </Box>
                <Divider />
                <List sx={{ p: 0 }}>
                    {activeChats.map((chat) => (
                        <ListItem 
                            button 
                            key={chat.id} 
                            selected={selectedChat?.id === chat.id}
                            onClick={() => handleSelectChat(chat)}
                            sx={{ 
                                borderBottom: '1px solid #f0f0f0',
                                borderLeft: selectedChat?.id === chat.id ? '4px solid #11224d' : '4px solid transparent',
                                bgcolor: selectedChat?.id === chat.id ? '#f0f7ff' : 'inherit',
                                '&.Mui-selected': {
                                    bgcolor: '#f0f7ff',
                                    '&:hover': { bgcolor: '#e3f2fd' }
                                }
                            }}
                        >
                            <ListItemAvatar>
                                <Badge 
                                    badgeContent={chat.unread || 0} 
                                    color="error"
                                    invisible={!chat.unread || chat.unread === 0}
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            fontSize: '0.75rem',
                                            minWidth: '20px',
                                            height: '20px'
                                        }
                                    }}
                                >
                                    <Avatar sx={{ bgcolor: chat.role === 'carrier' ? '#ffb300' : '#11224d' }}>
                                        <PersonIcon />
                                    </Avatar>
                                </Badge>
                            </ListItemAvatar>
                            <ListItemText 
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" fontWeight={chat.unread > 0 ? 'bold' : 'normal'}>
                                            {sanitizeText(chat.user_name) || `User ${chat.user_id}`}
                                        </Typography>
                                    </Box>
                                }
                                secondary={
                                    <Typography variant="caption" noWrap display="block">
                                        {sanitizeText(chat.last_message) || 'No messages yet'}
                                    </Typography>
                                } 
                            />
                            <Chip label={sanitizeText(chat.role)} size="small" sx={{ height: 20, fontSize: '0.6rem' }} />
                        </ListItem>
                    ))}
                </List>
            </Paper>

            <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {selectedChat ? (
                    <>
                        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
                            <Typography variant="subtitle1" fontWeight="bold">{sanitizeText(selectedChat.user_name)}</Typography>
                            <Typography variant="caption" color="text.secondary">Role: {sanitizeText(selectedChat.role)}</Typography>
                        </Box>
                        
                        <Box sx={{ flex: 1, p: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {messages.map((msg) => (
                                <Box 
                                    key={msg.id} 
                                    sx={{ 
                                        alignSelf: msg.sender === 'admin' ? 'flex-end' : 'flex-start',
                                        maxWidth: '70%'
                                    }}
                                >
                                    {msg.message_type === 'image' && msg.image_url && (
                                        <Box sx={{ mb: 0.5 }}>
                                            <img 
                                                src={msg.image_url.startsWith('data:') || msg.image_url.startsWith('http') ? msg.image_url : `${API_BASE_URL}${msg.image_url}`} 
                                                alt="Shared content" 
                                                style={{ 
                                                    maxWidth: '300px', 
                                                    maxHeight: '300px', 
                                                    borderRadius: '12px',
                                                    cursor: 'pointer',
                                                    border: '2px solid #e0e0e0'
                                                }}
                                                onClick={() => window.open(msg.image_url.startsWith('data:') || msg.image_url.startsWith('http') ? msg.image_url : `${API_BASE_URL}${msg.image_url}`, '_blank')}
                                            />
                                        </Box>
                                    )}
                                    {msg.message && (
                                        <Paper sx={{ 
                                            p: 1.5, 
                                            bgcolor: msg.sender === 'admin' ? '#11224d' : '#f5f5f5',
                                            color: msg.sender === 'admin' ? 'white' : 'text.primary',
                                            borderRadius: 2
                                        }}>
                                            <Typography variant="body2">{sanitizeText(msg.message)}</Typography>
                                        </Paper>
                                    )}
                                    <Typography variant="caption" sx={{ display: 'block', textAlign: msg.sender === 'admin' ? 'right' : 'left', mt: 0.5, color: '#999' }}>
                                        {dayjs.utc(msg.created_at).local().format('h:mm A')}
                                    </Typography>
                                </Box>
                            ))}
                            <div ref={messagesEndRef} />
                        </Box>

                        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                            {imagePreview && (
                                <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
                                    <img 
                                        src={imagePreview} 
                                        alt="Preview" 
                                        style={{ 
                                            maxWidth: '150px', 
                                            maxHeight: '150px', 
                                            borderRadius: '8px',
                                            border: '2px solid #e0e0e0'
                                        }}
                                    />
                                    <IconButton 
                                        size="small" 
                                        onClick={clearImageSelection}
                                        sx={{ 
                                            position: 'absolute', 
                                            top: -8, 
                                            right: -8, 
                                            bgcolor: 'white',
                                            boxShadow: 2,
                                            '&:hover': { bgcolor: '#f5f5f5' }
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                            <Box component="form" onSubmit={handleSendMessage} sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    accept={ALLOWED_IMAGE_TYPES.join(',')}
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                                <Tooltip title="Attach image (or paste with Ctrl+V)">
                                    <IconButton 
                                        size="small"
                                        onClick={() => fileInputRef.current?.click()}
                                        sx={{ color: '#11224d' }}
                                    >
                                        <ImageIcon />
                                    </IconButton>
                                </Tooltip>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Type a reply or paste an image..."
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    autoComplete="off"
                                />
                                <IconButton 
                                    type="submit" 
                                    color="primary" 
                                    disabled={!newMessage.trim() && !selectedImage}
                                >
                                    <SendIcon />
                                </IconButton>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#999', mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                💡 Tip: Press Ctrl+V to paste images directly
                            </Typography>
                        </Box>
                    </>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                        <Typography>Select a conversation to start chatting</Typography>
                    </Box>
                )}
            </Paper>
        </Box>
        <Snackbar
            open={errorSnackbar.open}
            autoHideDuration={4000}
            onClose={() => setErrorSnackbar({ ...errorSnackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <Alert onClose={() => setErrorSnackbar({ ...errorSnackbar, open: false })} severity="error" sx={{ width: '100%' }}>
                {errorSnackbar.message}
            </Alert>
        </Snackbar>
        </>
    );
};

export default AdminChat;